import React, { useCallback } from 'react';
import millify from 'millify';
import { Virtuoso } from 'react-virtuoso';
import type { FollowButtonSource } from '@soundxyz/utils/analytics';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { useMediaQuery } from '@context/mediaQuery';
import { useModal } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, gql, MintsDrivenModalDocument, useInfiniteQuery } from '@graphql/reactQuery';
import { MintsDrivenAffiliateBuyerFragmentDoc } from '@graphql/reactQuery';
import { getSingularOrPlural } from '@utils/helpers';
import { UserRow, UserRowSkeleton } from './shared/UserList/UserRow';

/*#__PURE__*/ gql(/* GraphQL */ `
  query MintsDrivenModal($playlistId: UUID!, $pagination: CursorConnectionArgs!) {
    playlistV2(id: $playlistId) {
      id
      numReferralPurchases
      numUsersReferred
      affiliateBuyers(pagination: $pagination) {
        edges {
          node {
            id
            ...MintsDrivenAffiliateBuyer
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }

  fragment MintsDrivenAffiliateBuyer on PlaylistAffiliateBuyer {
    id
    numAffiliateNftsPurchased
    user {
      ...UserModalRow
    }
  }
`);

type VirtuosoListContext = {
  sourceLocation: FollowButtonSource;
};

const userItemContent = (
  _: number,
  affiliateBuyer: FragmentType<MintsDrivenAffiliateBuyerFragmentDoc>,
  context: VirtuosoListContext,
) => {
  const { user, numAffiliateNftsPurchased } = getFragment(
    MintsDrivenAffiliateBuyerFragmentDoc,
    affiliateBuyer,
  );
  console;
  const editions = numAffiliateNftsPurchased
    ? `${millify(numAffiliateNftsPurchased)} ${getSingularOrPlural({
        word: 'edition',
        count: numAffiliateNftsPurchased,
      })}`
    : '';

  return <UserRow user={user} sourceLocation={context.sourceLocation} secondaryText={editions} />;
};

export const MintsDrivenModal = ({ playlistId }: { playlistId: string }) => {
  const { tablet } = useMediaQuery();
  const { closeCancelModal } = useModal();
  const [scrollParent, setScrollParent] = React.useState<HTMLDivElement | undefined>();

  const { orderedList, loadMoreNextPage, isLoadingNewPage, firstPage } = useInfiniteQuery(
    MintsDrivenModalDocument,
    {
      variables({ pageParam }) {
        return {
          playlistId,
          pagination: {
            first: 10,
            after: pageParam?.after || null,
          },
        };
      },
      filterQueryKey: {
        playlistId,
      },
      getNextPageParam({ data }) {
        return (
          data.playlistV2?.affiliateBuyers.pageInfo.hasNextPage && {
            after: data.playlistV2.affiliateBuyers.pageInfo.endCursor,
          }
        );
      },
      list({ playlistV2 }) {
        return playlistV2?.affiliateBuyers.edges.map(edge => edge.node);
      },
      uniq({ id }) {
        return id;
      },
      staleTime: 0,
    },
  );

  const LoadingFooter = useCallback(
    () =>
      isLoadingNewPage ? (
        <>
          <UserRowSkeleton />
          <UserRowSkeleton />
          <UserRowSkeleton />
        </>
      ) : null,
    [isLoadingNewPage],
  );

  if (!firstPage?.data.playlistV2) return null;
  const playlist = firstPage.data.playlistV2;
  const { numReferralPurchases: mintsDriven, numUsersReferred } = playlist;

  const title = `${millify(mintsDriven)} ${getSingularOrPlural({
    word: 'Mint',
    count: mintsDriven,
  })} Driven`;
  const subHeader = `${millify(numUsersReferred)} ${getSingularOrPlural({
    word: 'collector',
    count: numUsersReferred,
  })}`;

  const bodyContent = (
    <Virtuoso
      data={orderedList}
      itemContent={userItemContent}
      customScrollParent={scrollParent}
      endReached={loadMoreNextPage}
      context={{ sourceLocation: 'Mints Driven Modal' }}
      components={{ Footer: LoadingFooter }}
    />
  );

  return (
    <StandardModal
      title={title}
      subHeader={subHeader}
      body={bodyContent}
      closeModal={closeCancelModal}
      ref={r => {
        setScrollParent(r || undefined);
      }}
      isFullScreen={!tablet}
    />
  );
};
